import React from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import {
    getMyDraftAssets,
    AssetListItem,
    deleteDraftAsset,
} from '@/modules/Asset/services'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import DraftAssetCard from './DraftAssetCard'
import { paths, generatePathDatacentral } from '@/utils/path'
import { Loader2, Plus, FileText } from 'lucide-react'
import { useConfirmation } from '@/contexts/confirmation'
import { Button } from 'vnetwork-ui'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import toast from 'react-hot-toast'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'

const DraftsTab: React.FC = () => {
    const { t } = useTranslation('asset')
    const navigate = useNavigate()

    const { data: drafts, isLoading } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.ASSETS_DRAFT],
        queryFn: () =>
            getMyDraftAssets({
                page: 1,
                page_size: 100,
                q: '',
            }),
    })

    const deleteMutation = useMutationWithHandler(
        {
            mutationFn: deleteDraftAsset,
        },
        {
            reactQueryKeys: QUERY_KEYS.ASSETS_DRAFT,
            onSuccess: () => {
                toast.success(t('messages.deleteSuccess'))
            },
            onError: () => {
                toast.error(t('messages.deleteError'))
            },
        }
    )

    const handleEdit = (asset: AssetListItem) => {
        navigate(
            generatePathDatacentral(paths.assetEdit, {
                id: asset.asset_id,
            })
        )
    }

    const { setConfirmation } = useConfirmation()

    const handleDelete = (asset: AssetListItem) => {
        if (asset.asset_id) {
            setConfirmation({
                open: true,
                title: t('messages.deleteConfirmTitle'),
                description: t('messages.deleteConfirm'),
                onConfirm: () => {
                    return deleteMutation.mutateAsync(asset.asset_id)
                },
            })
        }
    }

    if (isLoading) {
        return (
            <div className="flex justify-center items-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
        )
    }

    if (!drafts?.results || drafts.results.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] text-center p-8 bg-muted/5 rounded-xl border-2 border-dashed border-muted/50 m-4">
                <div className="bg-muted/10 p-4 rounded-full mb-4 ring-8 ring-muted/5">
                    <FileText className="w-12 h-12 text-muted-foreground/40" />
                </div>
                <div className="space-y-2 mb-8">
                    <h3 className="text-xl font-semibold text-foreground">
                        {t('tabs.list')} {t('statuses.draft')}
                    </h3>
                    <p className="text-sm text-muted-foreground max-w-sm mx-auto">
                        {t('files.emptyDraftList')}
                    </p>
                </div>
                <Button onClick={() => navigate(paths.assetCreate)}>
                    <Plus className="w-5 h-5" />
                    {t('create')}
                </Button>
            </div>
        )
    }

    return (
        <div className="mt-2 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2">
            {drafts.results.map((draft) => (
                <DraftAssetCard
                    key={draft.asset_id}
                    asset={draft}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                />
            ))}
        </div>
    )
}

export default DraftsTab
